import { useMemo, useRef, useEffect, useState, useCallback, Fragment } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Tooltip, Polyline, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import './ReceiverOverviewMap.css';
import { hasEverSentStats, softwareLabel, locationLabel, trafficTypes } from '../utils/receiverSoftware';

/**
 * Global view of every receiver, for the admin All Receivers page.
 *
 * Green = active, red = inactive, each marker labelled with the receiver name. Where several
 * receivers sit close together the labels are fanned out and joined back to their marker by a
 * leader line, so a cluster stays readable instead of collapsing into overlapping text.
 *
 * This deliberately does NOT reuse ReceiverMarker: that component is bound to the live map
 * (selected-receiver highlighting, colour settings, range rings), and importing those concerns
 * here would couple the admin view to live-map state.
 */

/** Labels closer than this many pixels are treated as colliding and get fanned out. */
const COLLISION_PX = 46;
/** How far from the marker a displaced label sits. */
const LEADER_PX = 58;

/**
 * Whether a receiver counts as active.
 *
 * The two pages using this map describe status differently: the admin list carries
 * {@code connectionStatus} ("Active"/"Inactive") from getAllReceivers, while My Receivers derives
 * three states from {@code receiver.status} ("ACTIVE"/"CONNECTED"/"OFFLINE") with an
 * {@code isActive} boolean fallback. Hardcoding either would silently paint every marker red on
 * the other page, so this is only a default and callers may pass their own predicate.
 */
function defaultIsActive(receiver) {
  const conn = (receiver.connectionStatus || '').toLowerCase();
  if (conn) return conn === 'active';
  const status = (receiver.status || '').toUpperCase();
  if (status) return status === 'ACTIVE' || status === 'CONNECTED';
  return !!receiver.isActive;
}

function hostOf(meta) {
  return [meta?.hardwareModel, [meta?.osName, meta?.osVersion].filter(Boolean).join(' ')]
    .filter(Boolean).join(' / ');
}

/**
 * Marker icon. Colour is carried by CSS classes so it follows the theme, and the active marker
 * additionally has a filled centre dot: in the Mono theme status colours are greyscale, so colour
 * alone would make active and inactive indistinguishable.
 */
function receiverIcon(active) {
  return L.divIcon({
    className: '',
    html: `<span class="adm-marker ${active ? 'adm-marker-active' : 'adm-marker-inactive'}">
             ${active ? '<span class="adm-marker-core"></span>' : ''}
           </span>`,
    iconSize: [18, 18],
    iconAnchor: [9, 9],
  });
}

/** Fit the viewport to the visible receivers whenever that set changes. */
function FitToReceivers({ points }) {
  const map = useMap();
  const lastKey = useRef('');

  useEffect(() => {
    if (!points.length) return;
    // Re-fit when the SET changes (including via the owner filter), but not on every poll —
    // otherwise the map would yank itself back while the user is panning.
    const key = points.map(p => p.receiverUuid).sort().join(',');
    if (key === lastKey.current) return;
    lastKey.current = key;

    if (points.length === 1) {
      map.setView([points[0].latitude, points[0].longitude], 9);
    } else {
      map.fitBounds(points.map(p => [p.latitude, p.longitude]), {
        padding: [60, 60],
        maxZoom: 11,
      });
    }
  }, [points, map]);

  return null;
}

/**
 * Work out where each label should sit, in screen pixels relative to its marker.
 *
 * Receivers are frequently co-located — several at one airport, or a test bench with three on a
 * desk — and at world zoom every receiver in a country collapses onto a few pixels. Left alone the
 * permanent labels draw on top of each other and none of them can be read.
 *
 * So: project every marker to pixel space at the current zoom, group the ones within
 * COLLISION_PX, and fan each group's labels evenly around a circle. Singletons keep a plain offset
 * directly above the marker and get no leader line, because they do not need one.
 */
function useLabelLayout(points) {
  const map = useMap();
  const [layout, setLayout] = useState({});

  const recompute = useCallback(() => {
    if (!points.length) {
      setLayout({});
      return;
    }

    const projected = points.map(p => ({
      uuid: p.receiverUuid,
      pt: map.latLngToLayerPoint([p.latitude, p.longitude]),
    }));

    // Simple single-pass grouping: good enough for tens of receivers, and avoids pulling in a
    // clustering dependency for what is a labelling problem rather than a clustering one.
    const groups = [];
    const taken = new Set();
    for (let i = 0; i < projected.length; i++) {
      if (taken.has(i)) continue;
      const group = [i];
      taken.add(i);
      for (let j = i + 1; j < projected.length; j++) {
        if (taken.has(j)) continue;
        if (projected[i].pt.distanceTo(projected[j].pt) < COLLISION_PX) {
          group.push(j);
          taken.add(j);
        }
      }
      groups.push(group);
    }

    const next = {};
    for (const group of groups) {
      if (group.length === 1) {
        next[projected[group[0]].uuid] = { dx: 0, dy: -14, leader: false };
        continue;
      }
      // Fan the group's labels around a circle, starting at the top and going clockwise.
      const step = (2 * Math.PI) / group.length;
      group.forEach((idx, k) => {
        const angle = -Math.PI / 2 + k * step;
        next[projected[idx].uuid] = {
          dx: Math.round(Math.cos(angle) * LEADER_PX),
          dy: Math.round(Math.sin(angle) * LEADER_PX),
          leader: true,
        };
      });
    }
    setLayout(next);
  }, [points, map]);

  useEffect(recompute, [recompute]);
  useMapEvents({ zoomend: recompute, moveend: recompute });

  return layout;
}

/** The leader line from a marker to its displaced label. */
function LeaderLine({ point, offset }) {
  const map = useMap();
  if (!offset || !offset.leader) return null;

  const origin = map.latLngToLayerPoint([point.latitude, point.longitude]);
  const end = map.layerPointToLatLng(L.point(origin.x + offset.dx, origin.y + offset.dy));

  return (
    <Polyline
      positions={[[point.latitude, point.longitude], end]}
      className="adm-leader"
      interactive={false}
    />
  );
}

function ReceiverLayer({ points, meta, liveStats, isActiveFn, onSelectReceiver, onViewLive, onOpenTab }) {
  const layout = useLabelLayout(points);

  return (
    <>
      {points.map(r => {
        const m = meta[r.receiverUuid];
        const live = liveStats[r.receiverUuid];
        // Live websocket totals first (fresh every few seconds), else the daily row.
        const msgsToday = live?.totalMessagesToday ?? m?.messagesToday ?? null;
        const aircraftToday = live?.totalUniqueAircraftToday ?? m?.aircraftToday ?? null;
        const active = isActiveFn(r);
        const off = layout[r.receiverUuid] || { dx: 0, dy: -14, leader: false };
        const host = hostOf(m);
        return (
          <Fragment key={r.receiverUuid}>
            <LeaderLine point={r} offset={off} />
            <Marker position={[r.latitude, r.longitude]} icon={receiverIcon(active)}>
              <Tooltip
                permanent
                direction="center"
                offset={[off.dx, off.dy]}
                className={`adm-marker-label ${active ? 'is-active' : 'is-inactive'}`}
              >
                <span className="adm-label-name">{r.receiverName}</span>
                <span className="adm-label-status">{active ? 'Active' : 'Inactive'}</span>
              </Tooltip>
              <Popup>
                <div className="adm-popup">
                  <div className="adm-popup-title">{r.receiverName}</div>
                  <div className={`adm-popup-status ${active ? 'active' : 'inactive'}`}>
                    {active ? 'Active' : 'Inactive'}
                  </div>
                  <dl className="adm-popup-facts">
                    {r.emailAddress && <><dt>Owner</dt><dd>{r.emailAddress}</dd></>}
                    <dt>Software</dt>
                    <dd>{(() => {
                      const sw = softwareLabel(m, r);
                      if (!sw) return '—';
                      return sw.declared ? sw.label : `${sw.label} ${m.softwareVersion || ''}`.trim();
                    })()}</dd>
                    {host && <><dt>Hardware / OS</dt><dd>{host}</dd></>}
                    <dt>Traffic</dt>
                    <dd>{trafficTypes(r, live).filter(t => t.fresh).map(t => t.type).join(', ') || '—'}</dd>
                    <dt>Location</dt><dd>{locationLabel(r) || '—'}</dd>
                    <dt>Msgs today</dt>
                    <dd>{msgsToday != null ? Number(msgsToday).toLocaleString() : '—'}</dd>
                    <dt>Aircraft today</dt>
                    <dd>{aircraftToday != null ? Number(aircraftToday).toLocaleString() : '—'}</dd>
                  </dl>
                  <div className="adm-popup-actions">
                    <button onClick={() => onSelectReceiver && onSelectReceiver(r)}>View</button>
                    {onViewLive && <button onClick={() => onViewLive(r)}>View Live</button>}
                    {hasEverSentStats(m) ? (
                      <>
                        <button onClick={() => onOpenTab && onOpenTab(r, 'stats')}>Performance</button>
                        <button onClick={() => onOpenTab && onOpenTab(r, 'config')}>Device</button>
                      </>
                    ) : (
                      <button onClick={() => onOpenTab && onOpenTab(r, 'settings')}>Setup</button>
                    )}
                  </div>
                </div>
              </Popup>
            </Marker>
          </Fragment>
        );
      })}
    </>
  );
}

function ReceiverOverviewMap({
  receivers = [],
  meta = {},
  liveStats = {},
  ownerFilter = '',
  isActiveFn = defaultIsActive,
  emptyHint = '',
  onSelectReceiver,
  onViewLive = null,
  onOpenTab,
}) {
  const forOwner = useMemo(
    () => (ownerFilter
      ? receivers.filter(r => (r.emailAddress || '(unassigned)') === ownerFilter)
      : receivers),
    [receivers, ownerFilter]
  );

  const visible = useMemo(
    () => forOwner.filter(r => typeof r.latitude === 'number' && typeof r.longitude === 'number'),
    [forOwner]
  );

  // State what was left out. A receiver missing because it has no coordinates looks exactly like
  // one that does not exist, so say so rather than dropping it silently.
  const withoutPosition = forOwner.length - visible.length;
  const activeCount = visible.filter(isActiveFn).length;

  if (!visible.length) {
    return (
      <div className="admin-map-empty">
        No receivers with coordinates{ownerFilter ? ' for this owner' : ''}.
        {withoutPosition > 0 && ` ${withoutPosition} receiver(s) have no position set.`}
        {emptyHint && <div className="admin-map-empty-hint">{emptyHint}</div>}
      </div>
    );
  }

  return (
    <div className="admin-map-wrap">
      <div className="admin-map-legend">
        <span className="adm-legend-item">
          <span className="adm-marker adm-marker-active"><span className="adm-marker-core"></span></span>
          Active ({activeCount})
        </span>
        <span className="adm-legend-item">
          <span className="adm-marker adm-marker-inactive"></span>
          Inactive ({visible.length - activeCount})
        </span>
        {withoutPosition > 0 && (
          <span className="adm-legend-note">{withoutPosition} not shown (no position)</span>
        )}
      </div>

      <MapContainer className="admin-map" center={[20, 0]} zoom={2} scrollWheelZoom worldCopyJump>
        <TileLayer
          attribution='&copy; OpenStreetMap contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <FitToReceivers points={visible} />
        <ReceiverLayer
          points={visible}
          meta={meta}
          liveStats={liveStats}
          isActiveFn={isActiveFn}
          onSelectReceiver={onSelectReceiver}
          onViewLive={onViewLive}
          onOpenTab={onOpenTab}
        />
      </MapContainer>
    </div>
  );
}

export default ReceiverOverviewMap;
